import React, { useMemo } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('quote-to-close');

export default function QuoteToClose() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const trend = useNexusQuery('initiative-quote-to-close-trend', window);

  const data = useMemo(
    () =>
      trend.rows.map((r) => ({
        month: field(r, 'MonthKey'),
        lineConv: num(r, 'LineConversionPct'),
        dollarConv: num(r, 'DollarConversionPct'),
        days: num(r, 'AvgDaysToClose'),
        quotedLines: num(r, 'QuotedLines'),
        convertedLines: num(r, 'ConvertedLines'),
        quotedDollars: num(r, 'QuotedDollars'),
        bookedDollars: num(r, 'BookedDollars'),
      })),
    [trend.rows]
  );

  const totals = useMemo(() => {
    if (!data.length) return null;
    const quotedLines = data.reduce((s, d) => s + d.quotedLines, 0);
    const convertedLines = data.reduce((s, d) => s + d.convertedLines, 0);
    const quotedDollars = data.reduce((s, d) => s + d.quotedDollars, 0);
    const bookedDollars = data.reduce((s, d) => s + d.bookedDollars, 0);
    const withDays = data.filter((d) => d.days > 0);
    return {
      quotedLines,
      convertedLines,
      lineConv: quotedLines ? (convertedLines / quotedLines) * 100 : 0,
      dollarConv: quotedDollars ? (bookedDollars / quotedDollars) * 100 : 0,
      quotedDollars,
      bookedDollars,
      avgDays: withDays.length
        ? withDays.reduce((s, d) => s + d.days, 0) / withDays.length
        : 0,
    };
  }, [data]);

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <QueryState isLoading={trend.isLoading} error={trend.error} isEmpty={!data.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Line conversion"
              value={fmtPct(totals?.lineConv ?? 0)}
              sub={`${fmtInt(totals?.convertedLines ?? 0)} of ${fmtInt(totals?.quotedLines ?? 0)} quoted lines`}
              color={c.series[0]}
            />
            <StatTile
              label="Dollar conversion"
              value={fmtPct(totals?.dollarConv ?? 0)}
              sub={`${fmtMoney(totals?.bookedDollars ?? 0)} of ${fmtMoney(totals?.quotedDollars ?? 0)}`}
              color={c.series[1]}
            />
            <StatTile
              label="Average days to close"
              value={(totals?.avgDays ?? 0).toFixed(1)}
              sub="Quote issued to order booked"
            />
            <StatTile
              label="Quoted volume"
              value={fmtMoney(totals?.quotedDollars ?? 0)}
              sub="The base every conversion point acts on"
            />
          </SimpleGrid>
        </QueryState>

        <ChartCard
          title="Conversion and cycle time over time"
          caption="Watch this alongside SSP Deviation. If tightening pricing discipline costs us deals, conversion is where that cost surfaces first — which is exactly why the two KPIs belong to the same objective set."
          height={340}
        >
          <QueryState isLoading={trend.isLoading} error={trend.error} isEmpty={!data.length}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="month" stroke={c.axis} fontSize={10} />
                <YAxis
                  yAxisId="left"
                  stroke={c.axis}
                  fontSize={12}
                  tickFormatter={(v) => `${Math.round(v)}%`}
                  width={48}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={c.axis}
                  fontSize={12}
                  tickFormatter={(v) => `${Math.round(v)}d`}
                  width={44}
                />
                <Tooltip
                  content={
                    <ChartTooltip
                      formatter={(v, e) =>
                        e.dataKey === 'days' ? `${Number(v).toFixed(1)} days` : fmtPct(v)
                      }
                    />
                  }
                  cursor={{ fill: c.grid, opacity: 0.3 }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Legend iconType="circle" />
                <Bar
                  yAxisId="left"
                  dataKey="lineConv"
                  name="Line conversion"
                  fill={c.series[0]}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="dollarConv"
                  name="Dollar conversion"
                  fill={c.series[1]}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="days"
                  name="Avg days to close"
                  stroke={c.series[3]}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </QueryState>
        </ChartCard>
      </Stack>
    </KpiPage>
  );
}
